import { OverlayModule } from '@angular/cdk/overlay';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { ReportColumnType, ReportColumnVisibilityService } from '@services/report-column-visibility.service';
import { ReportSingleService } from '@services/report-single.service';
import { CheckboxComponent, IconSanitizerService, SearchInputComponent } from 'ngx-lib';

@Component({
    selector: 'app-column-visibility-filter',
    templateUrl: './column-visibility-filter.component.html',
    styleUrls: ['./column-visibility-filter.component.scss'],
    imports: [OverlayModule, SearchInputComponent, CheckboxComponent]
})
export class ColumnVisibilityFilterComponent implements OnInit {
    overlayOpen = signal(false);
    searchText = signal('');
    settingsPanel?: SafeHtml;

    filteredColumns = computed(() => {
        const searchValue = this.searchText();
        const columns = this.reportColumnVisibilityService.columnVisibilityState();

        if (!searchValue) {
            return columns;
        }

        return columns.filter((column: ReportColumnType) =>
            column.displayName.toLowerCase().includes(searchValue.toLowerCase())
        );
    });

    allSelected = computed(() => {
        const columns = this.reportColumnVisibilityService.columnVisibilityState();
        return columns.filter((column: ReportColumnType) => !column.isFrozen).every((column) => column.isSelected);
    });

    hasColumns = computed(() => this.reportColumnVisibilityService.availableColumns().length > 0);

    private readonly reportColumnVisibilityService = inject(ReportColumnVisibilityService);
    private readonly reportSingleService = inject(ReportSingleService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    constructor() {
        this.settingsPanel = this.iconSanitizer.getIcon('settingsPanel');
    }

    ngOnInit(): void {
        this.reportSingleService.ensureColumnVisibilityInitialized();
    }

    toggleOverlay(): void {
        this.overlayOpen.update((value) => !value);
    }

    onSearchTextChange(text: string): void {
        this.searchText.set(text);
    }

    onAllChange(value: boolean): void {
        const currentState = this.reportColumnVisibilityService.columnVisibilityState();
        const updatedColumns = currentState.map((column: ReportColumnType) => ({
            ...column,
            isSelected: column.isFrozen ? column.isSelected : value
        }));
        this.reportColumnVisibilityService.setColumnVisibilityState(updatedColumns);
    }

    onCheckboxChange(): void {
        const currentState = this.reportColumnVisibilityService.columnVisibilityState();
        this.reportColumnVisibilityService.setColumnVisibilityState([...currentState]);
    }

    apply(): void {
        this.reportColumnVisibilityService.updateGridColumnVisibility(
            this.reportColumnVisibilityService.columnVisibilityState()
        );
        this.reportSingleService.onHiddenColumnsChanged();
        this.closeOverlay();
    }

    closeOverlay(): void {
        this.overlayOpen.set(false);
        this.searchText.set('');
        this.reportColumnVisibilityService.setColumnVisibilityState([
            ...this.reportColumnVisibilityService.lastAppliedState()
        ]);
    }
}
